import { useMemo } from 'react';
import { useMutation, useQuery } from 'react-query';
import FloorPlanService from '../../../services/floorPlanService';
import UnitService from '../../../services/unitService';

import type { FloorPlan, UpdateUnitPayload } from './types';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { getAffordableFloorPlanProgramTypes } from '../../../utils/affordable';
import messages from './messages';
import { NaurEventStatus } from '../../../types/naur';
import { useNaurEventsForUnit } from '../../../hooks/data-fetching/useNaurWarnings';
import { useNaurEventsForBuilding } from '../../../hooks/data-fetching/useNaurEvents';

const floorPlanService = new FloorPlanService();
const unitService = new UnitService();

// --- Pure helpers ---
function buildNaurEventOption(event) {
  return {
    value: event.id,
    text: `Unit ${event.triggeringUnitDetails?.unitNumber ?? '---'} - ${
      event.triggeringUnitDetails?.bedroomCount ?? 0
    } BR`,
  };
}

function shouldDisplayNaurField({
  currentFloorPlanId,
  selectedFloorPlanId,
  floorPlans,
  currentlyAssociatedNaurEvent,
}): boolean {
  if (!currentFloorPlanId || !selectedFloorPlanId) return false;

  const selectedFloorPlan = floorPlans.find(
    (fp) => fp.id === selectedFloorPlanId,
  );
  if (!selectedFloorPlan) return false;

  const { isLIHTCFloorPlan: isSelectedLIHTC } =
    getAffordableFloorPlanProgramTypes(selectedFloorPlan);

  // NAUR field never shows when the selected floor plan is not LIHTC.
  // This covers Scenario 9.3 (LIHTC → Market Rate): even when the unit has an
  // existing association, the field is hidden and the association is cleared
  // automatically on save.
  if (!isSelectedLIHTC) return false;

  // Same LIHTC floor plan: show only when there is an existing association.
  if (currentFloorPlanId === selectedFloorPlanId) {
    return Boolean(currentlyAssociatedNaurEvent);
  }

  // Different floor plans (LIHTC target): only show for Market Rate → LIHTC transitions.
  const currentFloorPlan = floorPlans.find(
    (fp) => fp.id === currentFloorPlanId,
  );
  if (!currentFloorPlan) return false;

  const {
    isLIHTCFloorPlan: isCurrentLIHTC,
    isHUDFloorPlan,
    isRDFloorPlan,
  } = getAffordableFloorPlanProgramTypes(currentFloorPlan);

  return !isCurrentLIHTC && !isHUDFloorPlan && !isRDFloorPlan;
}

// --- Sub-hooks ---
function useNaurLockStatus(unitId): boolean {
  const { data: naurEventsData } = useNaurEventsForUnit(unitId || null);

  return Boolean(
    naurEventsData?.naurEvents?.some(
      (e) => e.status !== NaurEventStatus.SATISFIED,
    ),
  );
}

function useBuildingNaurEvents({ unitId, buildingId, naurFlag, open }) {
  const {
    data: buildingNaurEventsData,
    isLoading: isBuildingNaurEventsLoading,
  } = useNaurEventsForBuilding(
    buildingId,
    Boolean(naurFlag && open && buildingId),
  );

  const currentlyAssociatedNaurEvent = useMemo(() => {
    if (!buildingNaurEventsData?.naurEvents || !unitId) return null;
    return buildingNaurEventsData.naurEvents.find(
      (event) => event.satisfyingUnitId === unitId,
    );
  }, [buildingNaurEventsData, unitId]);

  return {
    buildingNaurEventsData,
    isBuildingNaurEventsLoading,
    currentlyAssociatedNaurEvent,
  };
}

function useNaurFieldOptions({
  showNaurField,
  buildingNaurEventsData,
  currentlyAssociatedNaurEvent,
  currentFloorPlanId,
  selectedFloorPlanId,
  currentUnitBedroomCount,
  floorPlans,
  intl,
}) {
  const qualifyingNaurEvents = useMemo(() => {
    if (!showNaurField || !buildingNaurEventsData?.naurEvents) return [];
    return buildingNaurEventsData.naurEvents.filter(
      (event) =>
        event.status === NaurEventStatus.OPEN &&
        (event.triggeringUnitDetails?.bedroomCount ?? 0) >=
          currentUnitBedroomCount,
    );
  }, [showNaurField, buildingNaurEventsData, currentUnitBedroomCount]);

  const naurEventOptions = useMemo(() => {
    const options = qualifyingNaurEvents.map(buildNaurEventOption);

    const currentEventInOptions = qualifyingNaurEvents.some(
      (e) => e.id === currentlyAssociatedNaurEvent?.id,
    );

    const additionalOptions =
      currentlyAssociatedNaurEvent && !currentEventInOptions
        ? [buildNaurEventOption(currentlyAssociatedNaurEvent)]
        : [];

    return [
      { value: '', text: intl.formatMessage(messages.naurEventNoneOption) },
      ...options,
      ...additionalOptions,
    ];
  }, [qualifyingNaurEvents, intl, currentlyAssociatedNaurEvent]);

  const isSelectedFloorPlanLIHTC = useMemo(() => {
    if (!selectedFloorPlanId) return false;
    const selectedFloorPlan = floorPlans.find(
      (fp) => fp.id === selectedFloorPlanId,
    );
    if (!selectedFloorPlan) return false;
    return getAffordableFloorPlanProgramTypes(selectedFloorPlan)
      .isLIHTCFloorPlan;
  }, [selectedFloorPlanId, floorPlans]);

  const hasSelectableEvents = useMemo(() => {
    if (qualifyingNaurEvents.length > 0) return true;
    if (currentlyAssociatedNaurEvent) {
      return (
        selectedFloorPlanId === currentFloorPlanId || isSelectedFloorPlanLIHTC
      );
    }
    return false;
  }, [
    qualifyingNaurEvents,
    currentlyAssociatedNaurEvent,
    selectedFloorPlanId,
    currentFloorPlanId,
    isSelectedFloorPlanLIHTC,
  ]);

  return { naurEventOptions, hasSelectableEvents };
}

// --- Main hook ---
export const useChangeFloorPlanNaurEvents = ({
  open,
  unitId,
  buildingId,
  currentFloorPlanId,
  selectedFloorPlanId,
  selectedNaurEventId,
  currentUnitBedroomCount,
  floorPlans,
  intl,
}) => {
  const { naur: naurFlag } = useFlags();

  const isLockedByNaur = useNaurLockStatus(unitId);

  const {
    buildingNaurEventsData,
    isBuildingNaurEventsLoading,
    currentlyAssociatedNaurEvent,
  } = useBuildingNaurEvents({ unitId, buildingId, naurFlag, open });

  const showNaurField =
    naurFlag &&
    shouldDisplayNaurField({
      currentFloorPlanId,
      selectedFloorPlanId,
      floorPlans,
      currentlyAssociatedNaurEvent,
    });

  const { naurEventOptions, hasSelectableEvents } = useNaurFieldOptions({
    showNaurField,
    buildingNaurEventsData,
    currentlyAssociatedNaurEvent,
    currentFloorPlanId,
    selectedFloorPlanId,
    currentUnitBedroomCount,
    floorPlans,
    intl,
  });

  const hasNaurAssociationChanged =
    showNaurField &&
    (selectedNaurEventId || null) !==
      (currentlyAssociatedNaurEvent?.id ?? null);

  return {
    isLockedByNaur,
    isBuildingNaurEventsLoading,
    showNaurField,
    naurEventOptions,
    hasNaurAssociationChanged,
    hasSelectableEvents,
    currentlyAssociatedNaurEvent,
  };
};

export const useFloorPlansQuery = (
  propertyId: string,
  organizationId: string,
  currentFloorPlanId?: string,
  options: Object = {},
): any => {
  return useQuery(
    ['floorPlans', organizationId, propertyId, currentFloorPlanId],
    () => floorPlanService.getAll(organizationId, propertyId, {}),
    {
      enabled: Boolean(propertyId && organizationId),
      select: (data: FloorPlan[]) => data || [],
      ...options,
    },
  );
};

export const useUnitFloorPlanScheduleQuery = (
  unitId: string,
  propertyId: string,
  organizationId: string,
  options: Object = {},
): any => {
  return useQuery(
    ['unitFloorPlanSchedule', unitId],
    () =>
      unitService.getUnitFloorPlanSchedule(organizationId, propertyId, unitId),
    {
      enabled: Boolean(unitId && propertyId && organizationId),
      ...options,
    },
  );
};

export const useHudContractOptionsQuery = (
  unitId: string,
  propertyId: string,
  organizationId: string,
  floorPlanId: string | null,
  options: Object = {},
): any => {
  return useQuery(
    ['hud-contract-options', unitId, propertyId, organizationId, floorPlanId],
    () =>
      unitService.getHudContractsForUnitAffordableSetup(
        propertyId,
        organizationId,
        unitId,
        floorPlanId ?? undefined,
      ),
    {
      enabled: Boolean(unitId && propertyId && organizationId),
      select: (data) => ({
        options: (data?.options ?? []).map((o) => ({
          text: o.text,
          value: o.value,
          contractNumber: o.contractNumber ?? '',
        })),
        selectedAffordableFundingId: data?.selectedAffordableFundingId ?? null,
      }),
      ...options,
    },
  );
};

export const useUpdateUnitMutation = ({
  propertyId,
  organizationId,
  unitId,
  options,
}: {
  propertyId: string,
  organizationId: string,
  unitId: string,
  options?: Object,
}): any => {
  return useMutation(
    (payload: UpdateUnitPayload) =>
      unitService.updateUnit(propertyId, organizationId, unitId, payload),
    {
      ...options,
    },
  );
};
